import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Plus, Trash2, Save, Pencil, MapPin, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import DestCityFilter from "../components/DestCityFilter";

const CURRENCIES = ["USD", "EUR", "GBP", "INR"];
const CUR_SYM = { USD: "$", EUR: "€", GBP: "£", INR: "₹" };

const DEFAULT_COUNTRIES = ["India", "Nepal", "Sri Lanka", "South Africa", "Kenya", "Thailand", "Vietnam", "Uganda", "Nigeria", "Bolivia"];

const empty = () => ({
  title: "", country: "", city: "", partner_id: "",
  price: 0, currency: "USD", duration_days: 2,
  description: "", inclusions: "", hero_image_url: "",
});

export default function WeekendTours() {
  const { user } = useAuth();
  const [tours, setTours] = useState([]);
  const [partners, setPartners] = useState([]);
  const [countries, setCountries] = useState(DEFAULT_COUNTRIES);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty());
  const [filter, setFilter] = useState({ destination: "", city: "" });

  const load = useCallback(async () => {
    const q = new URLSearchParams();
    if (filter.destination) q.set("destination", filter.destination);
    if (filter.city) q.set("city", filter.city);
    const [t, p, c] = await Promise.all([
      api.get(`/weekend-tours?${q.toString()}`).catch(() => ({ data: [] })),
      api.get("/partner-accounts").catch(() => ({ data: [] })),
      api.get("/accommodations/countries").catch(() => ({ data: { countries: [] } })),
    ]);
    setTours(t.data || []);
    setPartners(p.data || []);
    const list = c.data?.countries;
    setCountries(Array.isArray(list) && list.length ? list : DEFAULT_COUNTRIES);
  }, [filter.destination, filter.city]);
  useEffect(() => { load(); }, [load]);

  const canManage = ["FOUNDER", "BDOM"].includes(user.effective_role);
  const canDelete = (user.real_role || user.effective_role) === "FOUNDER";

  const startEdit = (t) => {
    setEditingId(t.id);
    setForm({
      title: t.title || "", country: t.country || "", city: t.city || "", partner_id: t.partner_id || "",
      price: t.price || 0, currency: t.currency || "USD", duration_days: t.duration_days || 2,
      description: t.description || "", inclusions: t.inclusions || "", hero_image_url: t.hero_image_url || "",
    });
    setOpen(true);
  };

  const cancel = () => { setOpen(false); setEditingId(null); setForm(empty()); };

  const submit = async () => {
    if (!form.title || !form.country || !form.city) return toast.error("Title, Country and City are required");
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0, duration_days: parseInt(form.duration_days) || 1 };
      if (editingId) await api.patch(`/weekend-tours/${editingId}`, payload);
      else await api.post("/weekend-tours", payload);
      toast.success(editingId ? "Tour updated" : "Tour created");
      cancel(); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Archive this weekend tour?")) return;
    try { await api.delete(`/weekend-tours/${id}`); await load(); toast.success("Archived"); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold">Weekend Tours</h1>
          <p className="text-xs text-[color:var(--aii-muted)]">Design custom-priced weekend excursions scoped to a Country, City and (optionally) a specific Partner. Visible in the Partner Portal.</p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2 flex-wrap">
            <DestCityFilter value={filter} onChange={setFilter} />
            <button onClick={() => { setOpen(true); setEditingId(null); setForm(empty()); }} data-testid="wt-new" className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5">
              <Plus size={14} /> New tour
            </button>
          </div>
        )}
        {!canManage && <DestCityFilter value={filter} onChange={setFilter} />}
      </div>

      {open && canManage && (
        <div className="aii-card p-6 space-y-4" data-testid="wt-form">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{editingId ? "Edit weekend tour" : "New weekend tour"}</h3>
            <button onClick={cancel}><X size={16} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Title *"><input className="input-aii" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="wt-title" /></Field>
            <Field label="Partner (optional — blank = all partners in that country)">
              <select className="input-aii" value={form.partner_id} onChange={e => setForm({ ...form, partner_id: e.target.value })} data-testid="wt-partner">
                <option value="">All partners in country</option>
                {partners.map(p => <option key={p.id} value={p.id}>{p.name} · {p.partner_profile?.country || ""}</option>)}
              </select>
            </Field>
            <Field label="Country *">
              <select className="input-aii" value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} data-testid="wt-country">
                <option value="">Choose country…</option>
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="City *"><input className="input-aii" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} data-testid="wt-city" /></Field>
            <Field label="Price"><input type="number" data-testid="wt-price" className="input-aii" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} /></Field>
            <Field label="Currency">
              <select data-testid="wt-currency" className="input-aii" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}>
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Duration (days)"><input type="number" min={1} data-testid="wt-duration" className="input-aii" value={form.duration_days} onChange={e => setForm({ ...form, duration_days: e.target.value })} /></Field>
            <Field label="Hero image URL"><input data-testid="wt-hero" className="input-aii" value={form.hero_image_url} onChange={e => setForm({ ...form, hero_image_url: e.target.value })} placeholder="https://…" /></Field>
            <Field label="Description" wide><textarea data-testid="wt-desc" className="input-aii" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></Field>
            <Field label="Inclusions" wide><textarea data-testid="wt-inclusions" className="input-aii" rows={2} value={form.inclusions} onChange={e => setForm({ ...form, inclusions: e.target.value })} placeholder="Transport, meals, entry tickets…" /></Field>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={cancel} className="px-3 py-2 border border-[color:var(--aii-border)] rounded text-sm">Cancel</button>
            <button onClick={submit} data-testid="wt-save" className="px-4 py-2 bg-brand text-white rounded hover:bg-brand-dark text-sm font-semibold flex items-center gap-1.5">
              <Save size={13} /> {editingId ? "Save changes" : "Create tour"}
            </button>
          </div>
        </div>
      )}

      <div className="aii-card overflow-x-auto">
        <table className="aii-table">
          <thead>
            <tr>
              <th>Title</th><th>Country · City</th><th>Partner scope</th><th>Duration</th><th>Price</th><th></th>
            </tr>
          </thead>
          <tbody>
            {tours.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[color:var(--aii-muted)]">No weekend tours yet — click <b>New tour</b> to create the first one.</td></tr>}
            {tours.map(t => (
              <tr key={t.id} data-testid={`wt-row-${t.id}`}>
                <td className="font-medium">
                  <div>{t.title}</div>
                  {t.description && <div className="text-xs text-[color:var(--aii-muted)] mt-0.5 line-clamp-2">{t.description}</div>}
                </td>
                <td><MapPin size={12} className="inline text-brand mr-1" /> {t.country} · {t.city}</td>
                <td>
                  {t.partner_id ? (
                    <span className="pill pill-green" data-testid={`wt-scope-${t.id}`}>{t.partner_name || t.partner_id.slice(0, 8)}</span>
                  ) : (
                    <span className="pill pill-amber">All partners</span>
                  )}
                </td>
                <td>{t.duration_days} day{t.duration_days === 1 ? "" : "s"}</td>
                <td className="font-semibold text-brand">{CUR_SYM[t.currency] || t.currency}{Number(t.price || 0).toLocaleString()}</td>
                <td className="text-right whitespace-nowrap">
                  {canManage && (
                    <div className="inline-flex gap-1">
                      <button onClick={() => startEdit(t)} className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded flex items-center gap-1"><Pencil size={11} /> Edit</button>
                      {canDelete && <button onClick={() => del(t.id)} data-testid={`wt-del-${t.id}`} className="text-xs px-2 py-1 border border-red-300 text-red-700 rounded flex items-center gap-1"><Trash2 size={11} /> Delete</button>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Field = ({ label, children, wide }) => (
  <div className={wide ? "sm:col-span-2" : ""}>
    <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);
